import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import documentService from '../../services/documentService';
import { getExtension } from '../../utils/fileTypes';

const RENDERED_TYPES = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'];

/** PDFs and photos are rendered by the API; every other type shows its extracted text. */
export function hasRenderedPreview(fileName) {
  return RENDERED_TYPES.includes(getExtension(fileName));
}

// Signed URLs live ~2 minutes; anything older than this is fetched again rather than risked.
const URL_FRESH_MS = 90_000;

/**
 * A rendered page (or the photo itself) inside the preview frame, with paging for PDFs.
 * `fallback` is shown when the API has no render for the file.
 */
export default function PagePreview({ doc, zoom, fallback = null }) {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ status: 'loading', url: null, pageCount: null, kind: null });
  const cache = useRef(new Map()); // page -> { url, pageCount, kind, fetchedAt }

  // A new document or version invalidates everything we know.
  useEffect(() => {
    cache.current.clear();
    setPage(1);
    setState({ status: 'loading', url: null, pageCount: null, kind: null });
  }, [doc.id, doc.currentVersionNo]);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.current.get(page);
    if (cached && Date.now() - cached.fetchedAt < URL_FRESH_MS) {
      setState({ status: 'ready', ...cached });
      return undefined;
    }

    setState((s) => ({ ...s, status: 'loading' }));
    documentService
      .preview(doc.id, { page })
      .then((result) => {
        if (cancelled) return;
        if (!result?.url || result.kind === 'none') {
          setState({ status: 'unavailable', url: null, pageCount: null, kind: 'none' });
          return;
        }
        const entry = { url: result.url, pageCount: result.pageCount, kind: result.kind, fetchedAt: Date.now() };
        cache.current.set(page, entry);
        setState({ status: 'ready', ...entry });
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, status: 'error' }));
      });

    return () => {
      cancelled = true;
    };
  }, [doc.id, doc.currentVersionNo, page]);

  if (state.status === 'unavailable') return fallback;

  const pageCount = state.pageCount;
  const canPage = state.kind === 'pdf' && pageCount > 1;
  const label = state.kind === 'pdf' ? `Page ${page}${pageCount ? ` / ${pageCount}` : ''}` : 'Image';

  return (
    <>
      <div className="preview-frame__pills">
        <span className="pill pill-dark">{label}</span>
        <span className="pill pill-outline" style={{ cursor: 'default' }}>
          {state.kind === 'pdf' ? 'Rendered page' : 'Original image'}
        </span>
      </div>

      {state.url && (
        <img
          key={state.url}
          className={`preview-page${state.status === 'loading' ? ' is-loading' : ''}`}
          src={state.url}
          alt={state.kind === 'pdf' ? `Page ${page} of ${doc.name}` : doc.name}
          style={{ transform: `scale(${zoom})` }}
          onError={() => {
            // Most likely an expired URL — forget it and fetch a fresh one.
            cache.current.delete(page);
            setState((s) => ({ ...s, status: 'error', url: null }));
          }}
        />
      )}

      {state.status === 'loading' && !state.url && (
        <div className="preview-frame__status">
          <span className="row" style={{ gap: 8 }}>
            <Loader2 size={16} className="spin" /> Rendering preview…
          </span>
        </div>
      )}
      {state.status === 'error' && (
        <div className="preview-frame__status">
          <span className="row" style={{ gap: 8 }}>
            The preview could not be loaded.
            <button type="button" className="link" onClick={() => setState((s) => ({ ...s, status: 'loading' }))}>
              Try again
            </button>
          </span>
        </div>
      )}

      {canPage && (
        <div className="preview-frame__nav">
          <button type="button" className="btn--icon" style={{ width: 28, height: 28 }} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft size={15} />
          </button>
          <span className="muted tnum" style={{ fontSize: 12, minWidth: 64, textAlign: 'center' }}>
            {page} of {pageCount}
          </span>
          <button type="button" className="btn--icon" style={{ width: 28, height: 28 }} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} disabled={page >= pageCount} aria-label="Next page">
            <ChevronRight size={15} />
          </button>
        </div>
      )}
    </>
  );
}
